/**
 * LA FACTURA ESCANEADA SE GUARDA. SIEMPRE.
 *
 * QUÉ PASÓ. El original de la factura viajaba dentro de la propia petición del
 * escaneo, y eso engordaba el POST hasta ~5 MB: en el móvil el primer intento de
 * cada sesión no salía del teléfono. Se sacó a su propia función de servidor
 * (`archive-invoice-file`) y se llamó sin esperarla, con un `catch {}` mudo
 * alrededor… y esa función nunca llegó a desplegarse. Devuelve 404. Durante dos
 * días TODA factura escaneada se apuntó con sus productos y sin su archivo:
 * «Ver factura» salía apagado y no había forma de mirar el papel. Nadie se
 * enteró porque el único que lo sabía era un `catch` vacío.
 *
 * LA LECCIÓN, y por eso existe este módulo: guardar el papel no puede depender
 * de que una petición suelta salga bien EN ESE INSTANTE. Se cae la red, se muere
 * la WebView de iOS al volver de la cámara, se cierra la pestaña, el móvil entra
 * en un ascensor. El archivo tiene que sobrevivir a todo eso.
 *
 * CÓMO. Los bytes se apuntan en IndexedDB ANTES de escanear y no se borran hasta
 * que el archivo está en el bucket Y la fila de `scanned_invoices` apunta a él.
 * Mientras tanto se reintenta: al terminar el escaneo, al arrancar la app, y
 * cuando vuelve la red. Sobrevive a un cierre de la app porque no vive en
 * memoria; sobrevive a que el escaneo se corte a medias porque la fila se puede
 * encontrar después por el `file_hash`.
 *
 * Es el MISMO camino en el móvil, en la tableta, en el navegador y en la vista
 * previa de Lovable: subida directa del navegador al bucket. No hay función de
 * servidor que pueda estar sin desplegar.
 *
 * Sin TTL a propósito, al revés que `pendingCaptureStore`: aquello es una foto a
 * medio hacer y caduca; esto es un documento que hay que guardar. Solo se tira
 * lo que ya no puede llegar a ninguna parte (ver `OUTBOX_MAX_AGE_MS`).
 */

const DB_NAME = "ulture_invoice_outbox";
const STORE = "pending";
const VERSION = 1;

/**
 * Después de una semana se tira. No es un plazo de reintento —se reintenta
 * mucho antes— sino el suelo de la basura: una factura cuya fila se borró
 * mientras el teléfono estaba apagado no tiene ya dónde engancharse, y sin este
 * tope se quedaría dando vueltas en IndexedDB para siempre.
 */
const OUTBOX_MAX_AGE_MS = 7 * 24 * 60 * 60 * 1000;

export interface InvoiceFileJob {
  /** Clave: el sha256 del archivo. Reescanear el mismo papel no duplica el apunte. */
  fileHash: string;
  companyId: string;
  /** Puede llegar vacío: si el escaneo se cortó, la fila se busca luego por `fileHash`. */
  invoiceId: string | null;
  mime: string;
  blob: Blob;
  savedAt: number;
  attempts: number;
}

/* Respaldo en memoria para el modo privado y los navegadores sin IndexedDB. Ahí
   el apunte no sobrevive a una recarga, pero el reintento dentro de la misma
   sesión sí funciona — que es mejor que el `catch {}` de antes. */
const memoryStore = new Map<string, InvoiceFileJob>();

function openDb(): Promise<IDBDatabase | null> {
  return new Promise((resolve) => {
    if (typeof indexedDB === "undefined") return resolve(null);
    try {
      const req = indexedDB.open(DB_NAME, VERSION);
      req.onupgradeneeded = () => {
        const db = req.result;
        if (!db.objectStoreNames.contains(STORE)) {
          db.createObjectStore(STORE, { keyPath: "fileHash" });
        }
      };
      req.onsuccess = () => resolve(req.result);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

async function withStore<T>(
  mode: IDBTransactionMode,
  fn: (store: IDBObjectStore) => IDBRequest<T>,
): Promise<T | null> {
  const db = await openDb();
  if (!db) return null;
  return new Promise((resolve) => {
    try {
      const tx = db.transaction(STORE, mode);
      const req = fn(tx.objectStore(STORE));
      req.onsuccess = () => resolve((req.result as T) ?? null);
      req.onerror = () => resolve(null);
    } catch {
      resolve(null);
    }
  });
}

/** Apuntar el archivo. Se llama ANTES de escanear: si el escaneo se cae, ya está guardado aquí. */
export async function enqueueInvoiceFile(
  job: Omit<InvoiceFileJob, "savedAt" | "attempts">,
): Promise<void> {
  const entry: InvoiceFileJob = { ...job, savedAt: Date.now(), attempts: 0 };
  memoryStore.set(entry.fileHash, entry);
  await withStore("readwrite", (s) => s.put(entry));
}

/**
 * Colgarle la factura a un apunte que se guardó sin ella.
 *
 * El id de la fila lo devuelve el servidor DESPUÉS de leer el documento, así que
 * en el momento de apuntar los bytes todavía no se sabe.
 */
export async function attachInvoiceIdToJob(fileHash: string, invoiceId: string): Promise<void> {
  const current = (await getInvoiceFileJob(fileHash)) ?? null;
  if (!current) return;
  const next: InvoiceFileJob = { ...current, invoiceId };
  memoryStore.set(fileHash, next);
  await withStore("readwrite", (s) => s.put(next));
}

export async function getInvoiceFileJob(fileHash: string): Promise<InvoiceFileJob | null> {
  const fromIdb = (await withStore<InvoiceFileJob>("readonly", (s) =>
    s.get(fileHash) as IDBRequest<InvoiceFileJob>,
  )) as InvoiceFileJob | null;
  return fromIdb ?? memoryStore.get(fileHash) ?? null;
}

/** Todo lo que sigue pendiente, lo caducado ya descontado. */
export async function listInvoiceFileJobs(): Promise<InvoiceFileJob[]> {
  const fromIdb = (await withStore<InvoiceFileJob[]>("readonly", (s) =>
    s.getAll() as IDBRequest<InvoiceFileJob[]>,
  )) as InvoiceFileJob[] | null;

  const byHash = new Map<string, InvoiceFileJob>();
  for (const job of memoryStore.values()) byHash.set(job.fileHash, job);
  for (const job of fromIdb ?? []) byHash.set(job.fileHash, job);

  const fresh: InvoiceFileJob[] = [];
  for (const job of byHash.values()) {
    if (Date.now() - job.savedAt > OUTBOX_MAX_AGE_MS) {
      void dropInvoiceFileJob(job.fileHash);
      continue;
    }
    fresh.push(job);
  }
  return fresh;
}

/** Un intento más. Solo para poder espaciar y para saber cuándo algo se ha atascado. */
export async function markInvoiceFileAttempt(fileHash: string): Promise<void> {
  const current = await getInvoiceFileJob(fileHash);
  if (!current) return;
  const next: InvoiceFileJob = { ...current, attempts: current.attempts + 1 };
  memoryStore.set(fileHash, next);
  await withStore("readwrite", (s) => s.put(next));
}

/** Guardado (o ya imposible de guardar): fuera de la cola. */
export async function dropInvoiceFileJob(fileHash: string): Promise<void> {
  memoryStore.delete(fileHash);
  await withStore("readwrite", (s) => s.delete(fileHash));
}
